import { X, Image as ImageIcon } from 'lucide-react';

interface Variant {
  id: string;
  name: string;
  link?: string;
}

interface GroupedMachineCardProps {
  brand: string;
  model: string;
  variants: Variant[];
  brandLink?: string;
  modelLink?: string;
  modelPicture?: string;
  onDelete?: (variantId: string) => void;
}

function MaybeLink({ href, children }: { href?: string; children: string }) {
  if (!href) return <span title={children}>{children}</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={children}
      className="hover:text-green-700 hover:underline"
    >
      {children}
    </a>
  );
}

function VariantChip({
  variant,
  onDelete,
}: {
  variant: Variant;
  onDelete?: (variantId: string) => void;
}) {
  return (
    <li className="group/variant inline-flex max-w-full items-center gap-1 rounded-md bg-neutral-50 py-0.5 pl-2 pr-1 text-xs font-medium text-neutral-700">
      <span className="truncate">
        <MaybeLink href={variant.link}>{variant.name}</MaybeLink>
      </span>
      {onDelete && (
        <button
          type="button"
          className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-red-700 opacity-60 transition-opacity hover:bg-red-500 hover:text-white hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:shadow-focus group-hover/variant:opacity-100"
          onClick={() => onDelete(variant.id)}
          aria-label={`Delete variant ${variant.name}`}
        >
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </li>
  );
}

export default function GroupedMachineCard({
  brand,
  model,
  variants,
  brandLink,
  modelLink,
  modelPicture,
  onDelete,
}: GroupedMachineCardProps) {
  return (
    <article className="flex w-full gap-3 rounded-xl border bg-card p-3">
      <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-neutral-50">
        {modelPicture ? (
          <img
            src={modelPicture}
            alt={`${brand} ${model}`}
            className="h-full w-full object-contain p-1.5 mix-blend-multiply"
          />
        ) : (
          <ImageIcon className="h-6 w-6 text-neutral-300" aria-hidden />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="min-w-0">
          <p className="truncate text-xs text-muted-foreground">
            <MaybeLink href={brandLink}>{brand}</MaybeLink>
          </p>
          <p className="truncate font-display text-base font-semibold leading-tight">
            <MaybeLink href={modelLink}>{model}</MaybeLink>
          </p>
        </div>
        <ul className="flex flex-wrap gap-1" aria-label={`${model} variants`}>
          {variants.map((variant) => (
            <VariantChip
              key={variant.id}
              variant={variant}
              onDelete={onDelete}
            />
          ))}
        </ul>
      </div>
    </article>
  );
}
